import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Edit2, Trash2, X, ChevronRight, LayoutList,
  Columns, ExternalLink, MessageSquare, Clock, RefreshCw,
  CheckCircle, AlertCircle, Link as LinkIcon, Tag,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { contentService, PIPELINE_STATUSES, PIPELINE_STATUS_META } from '../api/contentApi';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

// ── Helpers ───────────────────────────────────────────────────────────
const StatusBadge = ({ status, size = 'sm' }) => {
  const meta = PIPELINE_STATUS_META[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-medium capitalize ${meta.color}`}
          style={{ fontSize: size === 'sm' ? 11 : 13 }}>
      <span className="h-1.5 w-1.5 rounded-full inline-block" style={{ backgroundColor: meta.dot }} />
      {meta.label}
    </span>
  );
};

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// ── Content Form Modal ────────────────────────────────────────────────
const FORMATS = ['press_release', 'social_post', 'campaign_content', 'event_recap', 'blog', 'newsletter', 'video', 'other'];
const SOURCE_TYPES = ['manual', 'project', 'promotion', 'trending', 'other'];

const ContentFormModal = ({ isOpen, onClose, onSave, initial = null }) => {
  const empty = {
    title: '', summary: '', format: '', sourceType: 'manual', status: 'idea',
    targetAccountIds: [], tags: [], assigneeId: '',
    projectId: '', promotionId: '', publishAt: '',
    copy: { hook: '', body: '', cta: '', hashtags: [] },
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setForm(initial ? { ...empty, ...initial } : empty);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, isOpen]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setCopy = (key, val) => setForm(f => ({ ...f, copy: { ...f.copy, [key]: val } }));
  const csvArr = (val) => val.split(',').map(s => s.trim()).filter(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-2xl rounded-2xl border p-6 max-h-[92vh] overflow-y-auto"
           style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
            {initial ? 'Edit Content Item' : 'New Content Item'}
          </h3>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Core */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
            <input required type="text" value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="Content title…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Summary</label>
            <textarea value={form.summary} onChange={e => set('summary', e.target.value)}
              rows={2} placeholder="Brief description…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Format *</label>
              <select required value={form.format} onChange={e => set('format', e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                <option value="">Select format</option>
                {FORMATS.map(f => <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Source Type</label>
              <select value={form.sourceType} onChange={e => set('sourceType', e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                {SOURCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                {PIPELINE_STATUSES.map(s => (
                  <option key={s} value={s}>{PIPELINE_STATUS_META[s]?.label || s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Publish At</label>
              <input type="datetime-local" value={form.publishAt ? form.publishAt.slice(0, 16) : ''}
                onChange={e => set('publishAt', e.target.value ? new Date(e.target.value).toISOString() : '')}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Assignee ID</label>
              <input type="text" value={form.assigneeId || ''} onChange={e => set('assigneeId', e.target.value)}
                placeholder="User ID…"
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Project ID</label>
              <input type="text" value={form.projectId || ''} onChange={e => set('projectId', e.target.value)}
                placeholder="Linked project…"
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tags</label>
            <input type="text" value={(form.tags || []).join(', ')}
              onChange={e => set('tags', csvArr(e.target.value))}
              placeholder="Comma-separated tags…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>

          {/* Copy section */}
          <div className="rounded-lg border p-4 space-y-3" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: SYSTEM_COLORS.textMuted }}>Copy</p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Hook</label>
              <input type="text" value={form.copy.hook} onChange={e => setCopy('hook', e.target.value)}
                placeholder="Opening hook…"
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Body</label>
              <textarea value={form.copy.body} onChange={e => setCopy('body', e.target.value)}
                rows={3} placeholder="Main content…"
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">CTA</label>
                <input type="text" value={form.copy.cta} onChange={e => setCopy('cta', e.target.value)}
                  placeholder="Call to action…"
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                  style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Hashtags</label>
                <input type="text" value={(form.copy.hashtags || []).join(', ')}
                  onChange={e => setCopy('hashtags', csvArr(e.target.value))}
                  placeholder="#tag1, #tag2…"
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                  style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm"
              style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              {saving ? 'Saving…' : initial ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Review Modal ──────────────────────────────────────────────────────
const ReviewModal = ({ isOpen, onClose, onSubmit, itemTitle }) => {
  const [action, setAction] = useState('approve');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) { setAction('approve'); setComment(''); }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit({ action, comment });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md rounded-2xl border p-6"
           style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Review: {itemTitle}</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Decision</label>
            <select value={action} onChange={e => setAction(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
              <option value="approve">Approve</option>
              <option value="request_changes">Request Changes</option>
              <option value="comment">Comment Only</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Comment</label>
            <textarea value={comment} onChange={e => setComment(e.target.value)}
              rows={3} placeholder="Add a note…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm"
              style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              {saving ? 'Submitting…' : 'Submit Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Published Link Modal ──────────────────────────────────────────────
const PublishedLinkModal = ({ isOpen, onClose, onSave, itemTitle }) => {
  const [form, setForm] = useState({ accountId: '', platform: '', url: '', publishedAt: new Date().toISOString().slice(0, 16) });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setForm({ accountId: '', platform: '', url: '', publishedAt: new Date().toISOString().slice(0, 16) });
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ ...form, publishedAt: new Date(form.publishedAt).toISOString() });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md rounded-2xl border p-6"
           style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Record Published Link</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">URL *</label>
            <input required type="url" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
              placeholder="https://…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Platform</label>
              <input type="text" value={form.platform} onChange={e => setForm(f => ({ ...f, platform: e.target.value }))}
                placeholder="instagram, x…"
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Account ID</label>
              <input type="text" value={form.accountId} onChange={e => setForm(f => ({ ...f, accountId: e.target.value }))}
                placeholder="Account ID…"
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Published At</label>
            <input type="datetime-local" value={form.publishedAt}
              onChange={e => setForm(f => ({ ...f, publishedAt: e.target.value }))}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm"
              style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              {saving ? 'Saving…' : 'Save Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Content Item Card ─────────────────────────────────────────────────
const ContentCard = ({ item, onEdit, onDelete, onChangeStatus, onReview, onAddLink }) => {
  const [expanded, setExpanded] = useState(false);
  const nextStatus = PIPELINE_STATUSES[PIPELINE_STATUSES.indexOf(item.status) + 1];

  return (
    <div className="rounded-xl border overflow-hidden"
         style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2 p-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <StatusBadge status={item.status} />
            {item.format && (
              <span className="text-xs rounded-full px-2 py-0.5 capitalize"
                    style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted }}>
                {item.format.replace(/_/g, ' ')}
              </span>
            )}
          </div>
          <p className="font-semibold text-sm truncate" style={{ color: NEUTRAL_COLORS.black }}>{item.title}</p>
          {item.summary && (
            <p className="text-xs mt-0.5 line-clamp-2" style={{ color: SYSTEM_COLORS.textMuted }}>{item.summary}</p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {nextStatus && (
            <button onClick={() => onChangeStatus(item.id, nextStatus)}
              title={`Move to ${PIPELINE_STATUS_META[nextStatus]?.label}`}
              className="p-1 text-gray-400 hover:text-blue-600 rounded">
              <ChevronRight size={14} />
            </button>
          )}
          <button onClick={() => onReview(item)} title="Review"
            className="p-1 text-gray-400 hover:text-purple-600 rounded">
            <MessageSquare size={14} />
          </button>
          <button onClick={() => onAddLink(item)} title="Record published link"
            className="p-1 text-gray-400 hover:text-green-600 rounded">
            <LinkIcon size={14} />
          </button>
          <button onClick={() => onEdit(item)} title="Edit"
            className="p-1 text-gray-400 hover:text-blue-600 rounded">
            <Edit2 size={14} />
          </button>
          <button onClick={() => onDelete(item)} title="Delete"
            className="p-1 text-gray-400 hover:text-red-600 rounded">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Meta row */}
      <div className="flex items-center gap-4 px-4 pb-2 flex-wrap">
        {item.publishAt && (
          <span className="flex items-center gap-1 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
            <Clock size={11} /> {formatDate(item.publishAt)}
          </span>
        )}
        {(item.publishedLinks || []).length > 0 && (
          <span className="flex items-center gap-1 text-xs text-green-600">
            <CheckCircle size={11} /> {item.publishedLinks.length} published
          </span>
        )}
        {(item.tags || []).length > 0 && (
          <span className="flex items-center gap-1 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
            <Tag size={11} /> {item.tags.slice(0, 3).join(', ')}{item.tags.length > 3 ? '…' : ''}
          </span>
        )}
        {(item.reviewLog || []).length > 0 && (
          <span className="flex items-center gap-1 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
            <AlertCircle size={11} /> {item.reviewLog.length} review{item.reviewLog.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Expand: copy + published links */}
      {(item.copy?.hook || item.copy?.body || (item.publishedLinks || []).length > 0) && (
        <div className="border-t" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <button onClick={() => setExpanded(v => !v)}
            className="w-full text-left px-4 py-2 text-xs font-medium flex items-center gap-1"
            style={{ color: BRAND_COLORS.primary }}>
            {expanded ? '− Hide details' : '+ Show details'}
          </button>
          {expanded && (
            <div className="px-4 pb-4 space-y-3">
              {item.copy?.hook && (
                <div>
                  <p className="text-xs font-semibold mb-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>Hook</p>
                  <p className="text-sm" style={{ color: NEUTRAL_COLORS.black }}>{item.copy.hook}</p>
                </div>
              )}
              {item.copy?.body && (
                <div>
                  <p className="text-xs font-semibold mb-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>Body</p>
                  <p className="text-sm whitespace-pre-line" style={{ color: NEUTRAL_COLORS.black }}>{item.copy.body}</p>
                </div>
              )}
              {item.copy?.cta && (
                <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
                  <span className="font-semibold">CTA:</span> {item.copy.cta}
                </p>
              )}
              {(item.copy?.hashtags || []).length > 0 && (
                <p className="text-xs" style={{ color: BRAND_COLORS.primary }}>
                  {item.copy.hashtags.join(' ')}
                </p>
              )}
              {(item.publishedLinks || []).length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-1" style={{ color: SYSTEM_COLORS.textMuted }}>Published Links</p>
                  <div className="space-y-1">
                    {item.publishedLinks.map(link => (
                      <div key={link.id} className="flex items-center gap-2">
                        <a href={link.url} target="_blank" rel="noopener noreferrer"
                           className="text-xs text-blue-600 hover:underline flex items-center gap-1 truncate">
                          <ExternalLink size={10} />
                          {link.platform || link.url}
                        </a>
                        <span className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>
                          {formatDate(link.publishedAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Kanban Column ─────────────────────────────────────────────────────
const KanbanColumn = ({ status, items, onEdit, onDelete, onChangeStatus, onReview, onAddLink }) => {
  const meta = PIPELINE_STATUS_META[status];
  return (
    <div className="flex-shrink-0 w-72 rounded-xl border"
         style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, borderColor: SYSTEM_COLORS.borderSoft }}>
      <div className="flex items-center justify-between px-3 py-2.5 border-b"
           style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: meta.dot }} />
          <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: NEUTRAL_COLORS.black }}>
            {meta.label}
          </span>
        </div>
        <span className="text-xs rounded-full px-2 py-0.5 font-medium"
              style={{ backgroundColor: SYSTEM_COLORS.shellSurface, color: SYSTEM_COLORS.textMuted }}>
          {items.length}
        </span>
      </div>
      <div className="p-2 space-y-2 min-h-[120px]">
        {items.map(item => (
          <ContentCard key={item.id} item={item}
            onEdit={onEdit} onDelete={onDelete}
            onChangeStatus={onChangeStatus} onReview={onReview} onAddLink={onAddLink} />
        ))}
        {items.length === 0 && (
          <p className="text-center py-6 text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>Empty</p>
        )}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────
const VIEWS = ['kanban', 'list'];

const ContentPipeline = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');
  const [filterStatus, setFilterStatus] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await contentService.list(
        filterStatus.length ? { status: filterStatus } : {}
      );
      setItems(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load content items');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    try {
      if (editingItem) {
        const updated = await contentService.update(editingItem.id, form);
        setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
        toast.success('Item updated');
      } else {
        const created = await contentService.create(form);
        setItems(prev => [...prev, created]);
        toast.success('Content item created');
      }
      setEditingItem(null);
    } catch (err) {
      toast.error(err?.message || 'Save failed');
      throw err;
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await contentService.delete(item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleChangeStatus = async (itemId, newStatus) => {
    try {
      const updated = await contentService.changeStatus(itemId, newStatus);
      setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
      toast.success(`Moved to ${PIPELINE_STATUS_META[newStatus]?.label}`);
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleReview = async ({ action, comment }) => {
    try {
      const updated = await contentService.review(reviewTarget.id, { action, comment });
      setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
      toast.success('Review submitted');
      setReviewTarget(null);
    } catch (err) {
      toast.error(err?.message || 'Review failed');
      throw err;
    }
  };

  const handleAddLink = async (linkData) => {
    try {
      const updated = await contentService.addPublishedLink(linkTarget.id, linkData);
      setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
      toast.success('Published link recorded');
      setLinkTarget(null);
    } catch (err) {
      toast.error(err?.message || 'Failed to record link');
      throw err;
    }
  };

  const toggleStatusFilter = (status) => {
    setFilterStatus(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const itemsByStatus = (status) => items.filter(i => i.status === status);
  const statusCounts = PIPELINE_STATUSES.reduce((acc, s) => ({ ...acc, [s]: itemsByStatus(s).length }), {});

  return (
    <div className="space-y-5">
      {/* KPI strip */}
      <div className="flex gap-3 overflow-x-auto pb-1">
        {PIPELINE_STATUSES.map(s => {
          const meta = PIPELINE_STATUS_META[s];
          const active = filterStatus.includes(s);
          return (
            <button key={s} onClick={() => toggleStatusFilter(s)}
              className={`flex-shrink-0 rounded-lg border px-3 py-2 text-left transition-all ${active ? 'ring-2' : ''}`}
              style={{
                borderColor: active ? meta.dot : SYSTEM_COLORS.borderSoft,
                backgroundColor: active ? SYSTEM_COLORS.shellSurface : SYSTEM_COLORS.surfaceSubtle,
                ringColor: meta.dot,
              }}>
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.dot }} />
                <span className="text-xs font-medium" style={{ color: SYSTEM_COLORS.textMuted }}>{meta.label}</span>
              </div>
              <p className="text-lg font-bold" style={{ color: NEUTRAL_COLORS.black }}>{statusCounts[s]}</p>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-lg border p-1"
             style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          {[
            { id: 'kanban', icon: Columns },
            { id: 'list', icon: LayoutList },
          ].map(({ id, icon: Icon }) => (
            <button key={id} onClick={() => setView(id)}
              className="rounded-md p-1.5 transition-colors"
              style={{
                backgroundColor: view === id ? SYSTEM_COLORS.primaryTint : 'transparent',
                color: view === id ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
              }}>
              <Icon size={16} />
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {filterStatus.length > 0 && (
            <button onClick={() => setFilterStatus([])}
              className="rounded-lg border px-3 py-2 text-xs"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted }}>
              Clear filter
            </button>
          )}
          <button onClick={load}
            className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm"
            style={{ borderColor: SYSTEM_COLORS.border, color: SYSTEM_COLORS.textMuted }}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={() => { setEditingItem(null); setShowForm(true); }}
            className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            <Plus size={15} /> New Item
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2"
               style={{ borderColor: BRAND_COLORS.primary }} />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 rounded-xl border border-dashed"
             style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
            No content items yet — create your first one.
          </p>
        </div>
      ) : view === 'kanban' ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {PIPELINE_STATUSES.map(status => (
            <KanbanColumn key={status} status={status} items={itemsByStatus(status)}
              onEdit={(item) => { setEditingItem(item); setShowForm(true); }}
              onDelete={handleDelete}
              onChangeStatus={handleChangeStatus}
              onReview={(item) => setReviewTarget(item)}
              onAddLink={(item) => setLinkTarget(item)} />
          ))}
        </div>
      ) : (
        /* List view */
        <div className="rounded-xl border overflow-hidden"
             style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <table className="min-w-full">
            <thead style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
              <tr>
                {['Title', 'Format', 'Status', 'Publish At', 'Links', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: SYSTEM_COLORS.textMuted }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
              {(filterStatus.length ? items.filter(i => filterStatus.includes(i.status)) : items).map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>{item.title}</p>
                    {item.summary && <p className="text-xs truncate max-w-xs" style={{ color: SYSTEM_COLORS.textMuted }}>{item.summary}</p>}
                  </td>
                  <td className="px-4 py-3 text-sm capitalize" style={{ color: SYSTEM_COLORS.textMuted }}>
                    {(item.format || '').replace(/_/g, ' ')}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                  <td className="px-4 py-3 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                    {formatDate(item.publishAt)}
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                    {(item.publishedLinks || []).length}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {PIPELINE_STATUSES[PIPELINE_STATUSES.indexOf(item.status) + 1] && (
                        <button title="Advance status"
                          onClick={() => handleChangeStatus(item.id, PIPELINE_STATUSES[PIPELINE_STATUSES.indexOf(item.status) + 1])}
                          className="p-1 text-gray-400 hover:text-blue-600 rounded">
                          <ChevronRight size={14} />
                        </button>
                      )}
                      <button onClick={() => setReviewTarget(item)} className="p-1 text-gray-400 hover:text-purple-600 rounded">
                        <MessageSquare size={14} />
                      </button>
                      <button onClick={() => setLinkTarget(item)} className="p-1 text-gray-400 hover:text-green-600 rounded">
                        <LinkIcon size={14} />
                      </button>
                      <button onClick={() => { setEditingItem(item); setShowForm(true); }}
                        className="p-1 text-gray-400 hover:text-blue-600 rounded"><Edit2 size={14} /></button>
                      <button onClick={() => handleDelete(item)}
                        className="p-1 text-gray-400 hover:text-red-600 rounded"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ContentFormModal
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditingItem(null); }}
        onSave={handleSave}
        initial={editingItem}
      />

      <ReviewModal
        isOpen={!!reviewTarget}
        onClose={() => setReviewTarget(null)}
        onSubmit={handleReview}
        itemTitle={reviewTarget?.title || ''}
      />

      <PublishedLinkModal
        isOpen={!!linkTarget}
        onClose={() => setLinkTarget(null)}
        onSave={handleAddLink}
        itemTitle={linkTarget?.title || ''}
      />
    </div>
  );
};

export default ContentPipeline;
